import type { ModalNameType } from '@/modals/components/ModalProvider';
import type { BannerDtoType } from '@/queries/banners/banners.schema';
import { MODAL_DATA_PARAM, MODAL_PARAM } from '@/modals/consts';

// Backend can provide either presentation.order or the legacy priority value.
// Keep the UI order stable by preferring presentation.order and falling back to priority.
// 1 first by view, 2 - second by view, 3 - third by view
export const sortBannersByOrder = (banners: BannerDtoType[]) => {
  return [...banners].sort((a, b) => (a.presentation?.order ?? 0) - (b.presentation?.order ?? 0));
};

// Some CMS/banner redirects use the app modal deep-link format, for example
// `/?m=deposit` or `/?m=redeem&md=...`. These URLs represent client UI state,
// not a real page transition, so banner clicks should open the modal directly
// through `openModal` instead of navigating the user back through the lobby route.
//
// Return `null` for regular links so `DefaultBanners` can keep the normal Next
// `<Link>` behavior for internal pages, external promo URLs, and any malformed
// redirect values coming from CMS/backend data.
export function getRootModalLink(redirectUrl: string) {
  try {
    const url = new URL(redirectUrl, window.location.origin);
    const modalName = url.searchParams.get(MODAL_PARAM);

    // Only intercept same-origin root links that carry the modal query param.
    // Examples that should pass: `/?m=deposit`, `https://host/?m=support`.
    // Examples that should not pass: `/profile`, `/game/123`, external URLs.
    if (
      url.origin !== window.location.origin
      || url.pathname !== '/'
      || !modalName
    ) {
      return null;
    }

    const modalDataParam = url.searchParams.get(MODAL_DATA_PARAM);
    let modalData: unknown;

    if (modalDataParam) {
      try {
        modalData = JSON.parse(modalDataParam) as unknown;
      } catch {
        // Treat malformed modal payloads like missing payloads so a bad CMS URL
        // does not break banner clicks or prevent the modal from opening.
        modalData = undefined;
      }
    }

    return {
      modalName: modalName as ModalNameType,
      modalData,
    };
  } catch {
    return null;
  }
}
